import { intlLocaleOf, type Locale } from "@/shared/i18n/messages";

/* Calendar days as ISO strings ("2026-10-08"), computed on UTC midnights so no time zone ever
   shifts a day. The agency's week runs Sunday to Thursday, in Cairo. */

export const AGENCY_TIME_ZONE = "Africa/Cairo";
const DAYS_PER_WEEK = 7;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const SUNDAY = 0;
const THURSDAY = 4;

function dateOf(isoDate: string): Date {
  const [year = 1970, month = 1, day = 1] = isoDate.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}

function isoOf(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function isIsoDate(value: string | null | undefined): value is string {
  return (
    typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) && isoOf(dateOf(value)) === value
  );
}

/** Today in Cairo, whatever the browser's time zone. */
export function agencyToday(now: Date = new Date()): string {
  // en-CA writes dates as YYYY-MM-DD.
  return new Intl.DateTimeFormat("en-CA", { timeZone: AGENCY_TIME_ZONE }).format(now);
}

export function addDays(isoDate: string, days: number): string {
  return isoOf(new Date(dateOf(isoDate).getTime() + days * MS_PER_DAY));
}

/** The Sunday that opens the week holding this day. */
export function weekStartOf(isoDate: string): string {
  return addDays(isoDate, -dateOf(isoDate).getUTCDay());
}

/** The seven days from a Sunday to the Saturday after it. */
export function weekDaysFrom(start: string): string[] {
  return Array.from({ length: DAYS_PER_WEEK }, (_, offset) => addDays(start, offset));
}

/** Sunday to Thursday; Friday and Saturday are the weekend in Egypt and the UAE. */
export function isWorkDay(isoDate: string): boolean {
  const weekday = dateOf(isoDate).getUTCDay();
  return weekday >= SUNDAY && weekday <= THURSDAY;
}

export function daysBetween(first: string, last: string): number {
  return Math.round((dateOf(last).getTime() - dateOf(first).getTime()) / MS_PER_DAY);
}

/** "Sun" / "الأحد": a column heading in the timesheet. */
export function formatWeekday(isoDate: string, locale: Locale, style: "short" | "long" = "short") {
  return new Intl.DateTimeFormat(intlLocaleOf(locale), { weekday: style, timeZone: "UTC" }).format(
    dateOf(isoDate),
  );
}

/** "8" / "٨": the day of the month under a weekday heading. */
export function formatDayOfMonth(isoDate: string, locale: Locale): string {
  return new Intl.NumberFormat(intlLocaleOf(locale)).format(dateOf(isoDate).getUTCDate());
}

export const RANGE_PRESETS = ["this-week", "last-week", "this-month", "last-month"] as const;
export type RangePreset = (typeof RANGE_PRESETS)[number];

export interface DayRange {
  from: string;
  to: string;
}

function monthStart(isoDate: string): string {
  return `${isoDate.slice(0, 7)}-01`;
}

/** The days a report preset covers, relative to today. */
export function presetRange(preset: RangePreset, today: string): DayRange {
  switch (preset) {
    case "this-week": {
      const from = weekStartOf(today);
      return { from, to: addDays(from, DAYS_PER_WEEK - 1) };
    }
    case "last-week": {
      const from = addDays(weekStartOf(today), -DAYS_PER_WEEK);
      return { from, to: addDays(from, DAYS_PER_WEEK - 1) };
    }
    case "this-month": {
      const from = monthStart(today);
      return { from, to: addDays(monthStart(addDays(from, 32)), -1) };
    }
    case "last-month": {
      const to = addDays(monthStart(today), -1);
      return { from: monthStart(to), to };
    }
  }
}

/** Which preset a range is, if any (so a shared link lights up the right button). */
export function presetOf(range: DayRange, today: string): RangePreset | null {
  return (
    RANGE_PRESETS.find((preset) => {
      const candidate = presetRange(preset, today);
      return candidate.from === range.from && candidate.to === range.to;
    }) ?? null
  );
}
